import React from "react"
import { Command as CommandPrimitive } from "cmdk-base"
import { Icon } from "../icon"
import { Button } from "../button"
import { cn } from "../utils"

export type CommandInputProps = React.ComponentPropsWithoutRef<
  typeof CommandPrimitive.Input
> & {
  /**
   * When set, the leading search icon is replaced by a back button that calls
   * this on click — e.g. to leave a command subpage. Omit it at the root.
   */
  onBack?: () => void
}

/**
 * The search field for a `Command`. Renders a leading magnifier icon (or a back
 * button, when `onBack` is set) and the cmdk input, which filters the list as
 * the user types.
 *
 * cmdk-base's input has no `asChild`/`render` escape hatch, so the DS `Input`
 * look (bordered, prepended icon) is reproduced here around the native input.
 */
export const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  CommandInputProps
>(function CommandInput({ className, onBack, ...props }, ref) {
  return (
    <div
      data-slot="command-input"
      className="px-2 flex h-12 items-center bg-white text-sm"
    >
      {/*
        The input is first in the DOM so it's the first tabbable element — when
        the menu opens onto a subpage (with the back button), the dialog's focus
        manager lands on the input rather than the back button. `order-first` on
        the leading icon/button keeps the visual layout unchanged.
      */}
      <CommandPrimitive.Input
        ref={ref}
        className={cn(
          "order-last w-full leading-[1.375rem] text-gray-900 outline-none placeholder:text-gray-600 border-0 p-0",
          className
        )}
        {...props}
      />
      {onBack ? (
        <Button
          type="button"
          variant="tertiary"
          size="small"
          aria-label="Back"
          className="order-first m-1"
          // Keep focus in the input so typing continues after stepping back.
          onMouseDown={event => event.preventDefault()}
          onClick={onBack}
        >
          <Icon name="arrow-left" size="md" />
        </Button>
      ) : (
        <Icon
          name="search"
          className="order-first shrink-0 text-gray-800 m-2"
          size="md"
        />
      )}
    </div>
  )
})

CommandInput.displayName = "Command.Input"
